import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGlobe,
  faTrash,
  faSpinner,
  faLink,
  faTimes,
  faKey,
  faInbox,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { CopyToClipboard } from "react-copy-to-clipboard";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslate } from "../queries/use-translate";
import { useDeleteKey } from "../queries/use-delete-key";
import DeleteKey from "./DeleteKey";
import TranslationInput from "./common/TranslationInput";
import { FetchContext } from "../context/FetchContext";
import { useLanguage } from "../context/LanguageContext";
import { HighlightChars } from "./HighlightChars";
import { usePermission } from "../context/PermissionContext";
import { GridNavigationProvider } from "../context/GridNavigationContext";
import { isoToFlag } from "../util";

const RowAction = ({ icon, label, onClick, className, disabled }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    className={classNames(
      "inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 bg-white border border-gray-200 hover:text-primary hover:border-primary transition",
      disabled && "opacity-50 cursor-not-allowed",
      className
    )}
  >
    <FontAwesomeIcon icon={icon} className="w-3.5 h-3.5" />
  </button>
);

const Row = ({ data, index, style }) => {
  const {
    items,
    setPendingDeleteKey,
    setSelectedScreenshot,
    project,
    version,
    searchName,
    languageColIndex,
  } = data;
  const entry = items[index];
  const key = entry.item;
  const { mutate, isLoading } = useTranslate(project.id.toString(), version);
  const { displayLanguages, allowedLanguages } = useLanguage();
  const { hasAdminRight } = usePermission();

  const translate = () => mutate(key.id);

  const visibleTranslations = key.translations.filter(
    (t) => !displayLanguages || displayLanguages.includes(t.language.id)
  );

  return (
    <div style={style} className="px-4 pb-2">
      <div className="h-full flex flex-row rounded-xl border border-gray-200 bg-white overflow-hidden hover:border-gray-300 hover:shadow-sm transition">
        <div className="w-1/3 min-w-0 p-3 flex flex-col gap-2 border-r border-gray-200 bg-gray-50">
          <div className="flex flex-row items-start gap-2 min-w-0">
            <FontAwesomeIcon
              icon={faKey}
              className="w-3 h-3 mt-1 text-gray-400 flex-shrink-0"
            />
            <div className="flex-1 min-w-0 break-words text-sm font-semibold text-gray-800 leading-snug">
              <HighlightChars str={entry.item.name} search={searchName} />
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            {hasAdminRight && (
              <RowAction
                icon={faTrash}
                label="Delete key"
                onClick={() => setPendingDeleteKey(key)}
                className="hover:text-red-500 hover:border-red-300"
              />
            )}
            <RowAction
              icon={isLoading ? faSpinner : faGlobe}
              label="Auto translate"
              onClick={translate}
              disabled={isLoading}
              className={isLoading ? "animate-spin" : undefined}
            />
            <CopyToClipboard
              text={`${process.env.REACT_APP_URL}/projects/${project.id}#${key.name}`}
              onCopy={() => toast.success("Link copied")}
            >
              <RowAction icon={faLink} label="Copy link" />
            </CopyToClipboard>
          </div>

          {key.screenshots.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {key.screenshots.map((s) => (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => setSelectedScreenshot(s)}
                  className="rounded-md overflow-hidden border border-gray-200 hover:border-primary transition"
                >
                  <img
                    className="w-14 h-14 object-cover cursor-pointer"
                    src={s.screenshot.url}
                    alt={s.screenshot.title}
                  />
                </button>
              ))}
            </div>
          )}

          <div className="mt-auto text-xs text-gray-400 leading-none">
            {DateTime.fromISO(key.modifiedAt).toLocaleString(DateTime.DATE_MED)}
          </div>
        </div>

        <div className="flex flex-col flex-1 min-w-0">
          {visibleTranslations.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-xs text-gray-400 italic">
              No languages selected
            </div>
          )}
          {visibleTranslations.map((translation) => {
            const isBase = project.baseLanguageId === translation.language.id;
            return (
              <div
                key={translation.id}
                className="flex flex-row border-b last:border-b-0 border-gray-200 odd:bg-gray-50"
                style={{ height: 48 }}
              >
                <div
                  className={classNames(
                    "flex items-center justify-center gap-1.5 w-32 text-xs bg-white border-r border-gray-200",
                    isBase ? "font-bold text-gray-800" : "text-gray-600"
                  )}
                  title={translation.language.name}
                >
                  <span className="text-base leading-none">
                    {isoToFlag(translation.language.iso)}
                  </span>
                  <span className="uppercase tracking-wide">
                    {translation.language.iso}
                  </span>
                  {isBase && (
                    <span className="text-[9px] uppercase tracking-wide text-primary">
                      base
                    </span>
                  )}
                </div>
                <div className="flex-1 px-2 overflow-hidden">
                  <TranslationInput
                    search={searchName}
                    editable={allowedLanguages.includes(
                      translation.language.id
                    )}
                    id={translation.id}
                    projectId={project.id}
                    keyId={key.id}
                    value={translation.translation}
                    version={version}
                    rowIndex={index}
                    colIndex={languageColIndex?.get(translation.language.id)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const ProjectKeys = ({
  project,
  selectedFilterScreenshot,
  filter,
  sort,
  setSelectedScreenshot,
  version,
  searchName,
  setSearchName,
}) => {
  const fetchContext = useContext(FetchContext);
  const [listRef, setListRef] = useState(null);
  const location = useLocation();

  const { isLoading, data } = useQuery(
    [
      "project",
      project.id.toString(),
      "version",
      version?.toString(),
      "keys",
      { selectedFilterScreenshot, filter, sort },
    ],
    async () => {
      const { data } = await fetchContext.authAxios.get(
        `/projects/${project.id}/versions/${version}/keys`,
        {
          params: {
            screenshotId: selectedFilterScreenshot,
            untranslated: filter === "untranslate",
            sort,
          },
        }
      );
      return data;
    },
    {
      enabled: version !== null,
      onSettled: () => {},
    }
  );

  const [currentHash, setCurrentHash] = React.useState(() => location.hash);
  const [pendingDeleteKey, setPendingDeleteKey] = React.useState(null);

  const keys = useMemo(() => {
    if (!searchName) {
      return (data ?? []).map((item) => ({ item, matches: [], score: 1 }));
    }

    const q = searchName.toLowerCase();
    return (data ?? [])
      .filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          Boolean(
            d.translations.find((t) => t.translation.toLowerCase().includes(q))
          )
      )
      .map((item) => ({ item, matches: [], score: 1 }));
  }, [data, searchName]);

  const languageColIndex = useMemo(() => {
    const ids = new Set();
    (data ?? []).forEach((k) =>
      k.translations.forEach((t) => ids.add(t.language.id))
    );
    const sorted = Array.from(ids).sort((a, b) => a - b);
    const map = new Map();
    sorted.forEach((langId, idx) => map.set(langId, idx));
    return map;
  }, [data]);

  useEffect(() => {
    if (currentHash && keys && !isLoading && listRef) {
      const hash = currentHash.slice(1);
      const idx = keys.findIndex((v) => v.item.name === hash);
      if (idx >= 0) listRef.scrollToItem(idx, "start");
      setCurrentHash("");
    }
  }, [currentHash, keys, isLoading, listRef]);

  const { mutateAsync } = useDeleteKey(project.id.toString(), version);
  const { displayLanguages } = useLanguage();

  const deleteKey = async () => {
    try {
      await mutateAsync(pendingDeleteKey.id);
    } catch (e) {
      // no-op
    }
    setPendingDeleteKey(null);
  };

  const totalCount = (data ?? []).length;
  const filteredCount = keys.length;

  if (isLoading) {
    return (
      <div className="flex-1 bg-white rounded-2xl border border-gray-200 shadow-sm p-10 text-center text-gray-400 text-sm">
        Loading keys…
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col min-h-0 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="text-sm text-gray-600">
          {searchName ? (
            <span>
              <span className="font-semibold text-gray-800">
                {filteredCount}
              </span>{" "}
              of {totalCount} key{totalCount === 1 ? "" : "s"} matching{" "}
              <span className="italic text-gray-500">“{searchName}”</span>
            </span>
          ) : (
            <span>
              <span className="font-semibold text-gray-800">{totalCount}</span>{" "}
              key{totalCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 py-2">
        {filteredCount === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10 text-gray-500">
            <div className="w-14 h-14 rounded-2xl bg-primary bg-opacity-10 text-primary flex items-center justify-center mb-3">
              <FontAwesomeIcon
                icon={searchName ? faSearch : faInbox}
                className="text-2xl"
              />
            </div>
            <p className="text-base font-semibold text-gray-800">
              {searchName ? "No matching keys" : "No keys yet"}
            </p>
            <p className="text-sm text-gray-500 mt-1 max-w-sm">
              {searchName
                ? "Try a different search term or clear the filters."
                : "Add your first key to get started."}
            </p>
            {searchName && (
              <button
                type="button"
                onClick={() => setSearchName("")}
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white text-sm text-gray-700 px-4 py-2 hover:border-gray-300"
              >
                <FontAwesomeIcon icon={faTimes} className="w-3.5 h-3.5" />
                Clear search
              </button>
            )}
          </div>
        ) : (
          <GridNavigationProvider>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  ref={setListRef}
                  height={height}
                  itemCount={keys.length}
                  itemData={{
                    items: keys,
                    searchName,
                    setPendingDeleteKey,
                    setSelectedScreenshot,
                    project,
                    version,
                    languageColIndex,
                  }}
                  itemSize={
                    Math.max((displayLanguages ?? []).length, 3) * 48 + 8
                  }
                  width={width}
                >
                  {Row}
                </List>
              )}
            </AutoSizer>
          </GridNavigationProvider>
        )}
      </div>

      {pendingDeleteKey && (
        <DeleteKey
          name={pendingDeleteKey.name}
          onCancel={() => setPendingDeleteKey(null)}
          onConfirm={deleteKey}
        />
      )}
    </div>
  );
};
